/**
 * The benefit year — **one date, one place** (amendment 20 §3.3).
 *
 * Today three agents each carry their own idea of when benefits reset: Recall's
 * window (1 Oct), the Treatment Coordinator's *start talking about year-end from
 * 1 September*, and Invisalign's day-40 email, which reads *"the year-end window
 * set on Recall"*. Three settings, one fact, and nothing keeping them in step.
 *
 * So the date lives here and the three lead windows are **derived from it**. At
 * the default they reproduce exactly today's values — 1 Oct · 1 Oct · 1 Sep —
 * and changing the date moves all three at once.
 */

export interface BenefitYear {
  /** 1–12. */
  month: number;
  /** 1–31. */
  day: number;
}

/** Most plans reset on 31 December. */
export const BENEFIT_YEAR_DEFAULT: BenefitYear = { month: 12, day: 31 };

/**
 * How far ahead each agent may start mentioning unused benefits. **Fixed by ELVA
 * in V1 and shown read-only** — the practice chooses the date, not the lead time.
 */
export const LEAD_MONTHS = {
  recall: 3,
  invisalign: 3,
  treatment: 4,
} as const;

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export const MONTH_OPTIONS = MONTHS.map((label, i) => ({ value: String(i + 1), label }));

/** How many days a month has, ignoring leap years — this is a recurring date, not a moment. */
export function daysInMonth(month: number): number {
  return [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month - 1] ?? 31;
}

export function formatBenefitDate({ month, day }: BenefitYear): string {
  return `${String(day)} ${MONTHS[month - 1] ?? ''}`;
}

/** Short form for the derived rows — `1 October`. */
export function leadDate(reset: BenefitYear, monthsBefore: number): string {
  // The window COVERS `monthsBefore` months and ends on the reset date, so with a
  // 31 December reset a three-month window is October–December and opens on
  // 1 October — not 1 September, which would be four months.
  const zero = reset.month - monthsBefore;
  const month = ((zero % 12) + 12) % 12;
  // It opens on the first of that month, which is how every agent's copy already
  // reads ("from 1 September") and what the boards draw.
  return `1 ${MONTHS[month] ?? ''}`;
}

/** The three rows the Insurance card renders read-only beneath the date. */
export function benefitLeadRows(reset: BenefitYear): { agent: string; from: string; note: string }[] {
  return [
    {
      agent: 'Recall',
      from: leadDate(reset, LEAD_MONTHS.recall),
      note: `mentions unused benefits from ${leadDate(reset, LEAD_MONTHS.recall)}`,
    },
    {
      agent: 'Invisalign Coordinator',
      from: leadDate(reset, LEAD_MONTHS.invisalign),
      note: `from ${leadDate(reset, LEAD_MONTHS.invisalign)}`,
    },
    {
      agent: 'Treatment Coordinator',
      from: leadDate(reset, LEAD_MONTHS.treatment),
      note: `from ${leadDate(reset, LEAD_MONTHS.treatment)}`,
    },
  ];
}

/** Module-level, because the TC and Invisalign pages read what Insurance owns. */
export const benefitYearStore: BenefitYear = { ...BENEFIT_YEAR_DEFAULT };
